import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Stack,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbar,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface DataTableProps {
  title?: string;
  subtitle?: string;
  columns: GridColDef[];
  rows: any[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationChange: (model: GridPaginationModel) => void;
  loading?: boolean;
  error?: string | null;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  hideSearchBar?: boolean;
  hideFooter?: boolean;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  onRowClick?: (params: any) => void;
  sx?: any;
  action?: React.ReactNode;
}

export default function DataTable({
  title,
  subtitle,
  columns,
  rows,
  rowCount,
  paginationModel,
  onPaginationChange,
  loading = false,
  error = null,
  onSearchChange,
  searchPlaceholder = 'Search...',
  hideSearchBar = false,
  hideFooter = false,
  rowHeight = 52,
  disableSelectionOnClick = true,
  onRowClick,
  sx = {},
  action,
}: DataTableProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearchChange?.('');
  };

  return (
    <Card sx={sx}>
      {(title || subtitle || !hideSearchBar || action) && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={action}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}

      <CardContent sx={{ p: 0 }}>
        {/* Search Bar */}
        {!hideSearchBar && onSearchChange && (
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{
                flex: 1,
                maxWidth: 400,
              }}
            />
            {searchQuery && (
              <Button
                variant="text"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearSearch}
              >
                Clear
              </Button>
            )}
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* DataGrid */}
        {!loading && !error && (
          <Box
            sx={{
              width: '100%',
              height: Math.max(300, rows.length * rowHeight + 100),
              '& .MuiDataGrid-root': {
                border: 'none',
                borderRadius: 0,
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: 'background.default',
                borderColor: 'divider',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'action.hover',
                  cursor: onRowClick ? 'pointer' : 'default',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderColor: 'divider',
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationChange}
              rowCount={rowCount}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationMode="server"
              loading={loading}
              disableRowSelectionOnClick={disableSelectionOnClick}
              onRowClick={onRowClick}
              slots={{
                toolbar: hideSearchBar ? undefined : GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: false,
                },
              }}
              sx={{
                height: '100%',
              }}
            />
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && rows.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <Box
              sx={{
                fontSize: '3rem',
                mb: 1,
              }}
            >
              📭
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              No data found. Try adjusting your search or filters.
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
